import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

// Sesión completa del admin: par JWT + usuario logueado.
// Vive en sessionStorage para que se vaya al cerrar la pestaña — no es un cookie
// de larga duración. El refresh token igual habilita reanudación dentro de la sesión.
const STORAGE_KEY = "novasis-admin-session";

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: "superadmin" | "operator";
}

export interface AdminSession {
  accessToken: string;
  refreshToken: string;
  user: AdminUser;
}

interface TokenContextValue {
  session: AdminSession | null;
  setSession: (s: AdminSession) => void;
  clear: () => void;
}

const TokenContext = createContext<TokenContextValue | null>(null);

function readStored(): AdminSession | null {
  if (typeof window === "undefined") return null;
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<AdminSession>;
    if (!parsed?.accessToken || !parsed?.refreshToken) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    // Tolera sesiones sin user.name (ej. usuarios viejos sin name en DB)
    if (!parsed.user) {
      parsed.user = { id: "", email: "", name: "", role: "operator" };
    }
    return parsed as AdminSession;
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function TokenProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<AdminSession | null>(() => readStored());

  const setSession = useCallback((s: AdminSession) => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    setSessionState(s);
  }, []);

  const clear = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setSessionState(null);
  }, []);

  // Sync entre pestañas — si en otra tab hacés login/logout, esta se entera.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== STORAGE_KEY) return;
      setSessionState(readStored());
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(
    () => ({ session, setSession, clear }),
    [session, setSession, clear],
  );
  return <TokenContext.Provider value={value}>{children}</TokenContext.Provider>;
}

export function useSession() {
  const ctx = useContext(TokenContext);
  if (!ctx) throw new Error("useSession must be used inside TokenProvider");
  return ctx;
}

export const ADMIN_SESSION_STORAGE_KEY = STORAGE_KEY;
